/* eslint-disable react-hooks/exhaustive-deps */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { getFormFields } from './AdvFormUtil';
import AdvFormActionButtons from './AdvFormActionButtons';
import AdvFormHeader from './AdvFormHeader';
import advFormStyles from '../../../styles/AdvancedFormSearch/AdvanceFormSearchStyles';
import AdvAppFormItem from './AdvAppFormItem';

const AdvFormSearch = ({
  handleFormChange = console.log,
  advFormData,
  setAdvFormData,
  handleAdvFormSearchClick,
  toggleAdvFormSearch,
  intlFormData,
  retainValues,
  acsMgmt,
  loginData,
  isForFileTrack = false,
  onItemClick,
  resetHandlerTriggered,
  savedSearchList,
}) => {
  const [pageData, setPageData] = useReducer(
    (value, newValue) => ({ ...value, ...newValue }),
    advFormData
  );

  useEffect(() => {
    handleFormChange(pageData);
  }, [pageData]);

  const enableSaveSearch =  pageData?.saveSearchProp?.enable || false;

  const [showSaveListPopup, setShowSaveListPopup] = useState(false);
  const [showSaveSearch, setShowSaveSearch] = useState(false);
  const handleFormSubmit = () => {
    const newPageData = { ...pageData };
    newPageData.formFields.forEach((field) => {
      if (field.value?.length < field?.minLength) {
        // eslint-disable-next-line no-param-reassign
        field.value = '';
      }
    });
    setAdvFormData(newPageData);
    handleAdvFormSearchClick(newPageData);
  };

  const [selectedItem, setSelectedItem] = useState(null);

  const handleFormReset = () => {
    const newFormFields = pageData.formFields.map((field, i) => {
      const intlField = intlFormData.formFields[i];
      if (field.type === 'date-time-range') {
        return { ...field, value: '' };
      }
      if (field.type === 'multi-select' || field.type === 'multi-selectV2') {
        const value = intlField.value;
        let selectedIds = [];
        if (value && value.includes(',')) {
          selectedIds = value.split(',').map(item => Number.parseInt(item, 10));
        } else if (value) {
          selectedIds = [Number.parseInt(value, 10)];
        }
        return { ...field, value, selectedIds };
      }
      if (field.type === 'AdvFormSearchItemV2') {
        return { ...field, value: intlField.value, displayValue: intlField.displayValue };
      }
      return { ...field, value: intlField.value };
    });
  
    // Reset suggestion lists
    const decidingOthersSuggestFields = _.filter(newFormFields, field => !_.isNil(field.decidesSuggestListFor));
    decidingOthersSuggestFields.forEach(field => {
      const i = field.decidesSuggestListFor;
      newFormFields[i] = { ...newFormFields[i], suggestionList: [] };
    });
  
    const newPageData = { ...pageData, formFields: newFormFields };
    setPageData(newPageData);
    setAdvFormData(newPageData);
    setSelectedItem(null);
    if (resetHandlerTriggered) resetHandlerTriggered(newPageData);
  };

  useEffect(() => {
    // To retain the values on re-opening the form uncomment below
    // if (!retainValues && !isForFileTrack) {
    if (!retainValues) {
      handleFormReset();
    }
  }, [retainValues]);

  useEffect(() => {
    if (pageData.formFields?.length > 0) {
      setPageData(advFormData);
    }
  }, [advFormData, advFormData.formFields]);

  const formFields = getFormFields(pageData, setPageData, acsMgmt, loginData, false);

  return (
    <div id="AdvFormSearch" className="pad-1-lr" css={advFormStyles['.container']}>
      <AdvFormHeader
        pageData={pageData}
        setPageData={setPageData}
        toggleAdvFormSearch={toggleAdvFormSearch}
        enableSaveSearch={enableSaveSearch}
        savedSearchList={savedSearchList}
        showPopup={showSaveListPopup}
        setShowPopup={setShowSaveListPopup}
        onItemClick={onItemClick}
        handleFormReset={handleFormReset}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
      />
      <div className="col-md-12">
        {formFields}
        {isForFileTrack && (
        <AdvAppFormItem
          pageData={pageData}
          setPageData={setPageData}
          acsMgmt={acsMgmt}
          loginData={loginData}
        />
        )}
      </div>
      <AdvFormActionButtons
        leftButtonName="Reset"
        rightButtonName="Search"
        handleFormLeftButton={handleFormReset}
        handleFormRightButton={handleFormSubmit}
        pageData={pageData}
        setPageData={setPageData}
        toggleAdvFormSearch={toggleAdvFormSearch}
        enableSaveSearch={enableSaveSearch}
        savedSearchList={savedSearchList}
        showPopup={showSaveSearch}
        setShowPopup={setShowSaveSearch}
        onItemClick={onItemClick}
        handleFormReset={handleFormReset}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
      />
    </div>
  );
};

AdvFormSearch.displayName = 'AdvFormSearch';

AdvFormSearch.propTypes = {
  advFormData: PropTypes.shape({
    formFields: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
      })),
  }).isRequired,
  setAdvFormData: PropTypes.func.isRequired,
  handleAdvFormSearchClick: PropTypes.func.isRequired,
  toggleAdvFormSearch: PropTypes.func.isRequired,
  // eslint-disable-next-line react/boolean-prop-naming
  retainValues: PropTypes.bool,
  intlFormData: PropTypes.shape({
    formFields: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        displayValue: PropTypes.string,
        selectedIds: PropTypes.arrayOf,
      })
    ),
  }),
  acsMgmt: PropTypes.node,
  handleFormChange: PropTypes.func,
  loginData: PropTypes.shape({}),
  savedSearchList: PropTypes.arrayOf(PropTypes.shape({})),
  isForFileTrack: PropTypes.bool.isRequired,
  showSavedSearch: PropTypes.bool,
  onItemClick: PropTypes.func,
  resetHandlerTriggered: PropTypes.func,
};

export default AdvFormSearch;
